import { JsonFormsCore } from "@jsonforms/core";
import { useCallback, useEffect, useRef, useState } from "react";
import { useFormContext } from "react-hook-form";

import FieldLabel from "@/components/primitives/InputLabel";
import { WeldJsonForms } from "@/components/modules/JsonForms";
import { JsonForm } from "@/integrations/forms";

export function JsonFormRenderer({ field }: { field: JsonForm }) {
  const { register, setValue, trigger, unregister, getValues } =
    useFormContext();
  const prevKeysRef = useRef<string[]>([]);

  const getInitialData = (): Record<string, unknown> => {
    const existing = getValues(field.name);
    if (
      existing &&
      typeof existing === "object" &&
      Object.keys(existing).length > 0
    ) {
      return existing as Record<string, unknown>;
    }
    return field.data ?? {};
  };

  const dataRef = useRef<Record<string, unknown>>(getInitialData());
  const errorsRef = useRef<JsonFormsCore["errors"]>([]);
  const [formData, setFormData] = useState<Record<string, unknown>>(
    dataRef.current,
  );

  useEffect(() => {
    register(field.name, {
      validate: () => {
        if (field.required && Object.keys(dataRef.current).length === 0) {
          return "This field is required";
        }
        if (errorsRef.current?.length) {
          return errorsRef.current[0]?.message ?? "Validation error";
        }
        return true;
      },
    });
    // Deliberately not unregistering `field.name` here: this component can
    // unmount/remount when navigating between wizard steps, and unregistering
    // would wipe out the values the user already entered.
  }, [field.name, field.required, register]);

  const handleChange = useCallback(
    (data: Record<string, unknown>, errors?: JsonFormsCore["errors"]) => {
      const createNamespacedKey = (key: string) => `${field.name}[${key}]`;
      const newKeys = Object.keys(data ?? {}).map(createNamespacedKey);
      dataRef.current = data ?? {};
      setFormData(data ?? {});

      prevKeysRef.current.forEach((namespacedKey) => {
        if (!newKeys.includes(namespacedKey)) {
          unregister(namespacedKey);
        }
      });

      Object.entries(data ?? {}).forEach(([key, value]) => {
        const namespacedKey = createNamespacedKey(key);
        register(namespacedKey);
        setValue(namespacedKey, value, {
          shouldValidate: true,
          shouldDirty: true,
        });
      });

      prevKeysRef.current = newKeys;

      errorsRef.current = errors ?? [];
      trigger(field.name);
    },
    [field.name, register, setValue, trigger, unregister],
  );

  useEffect(() => {
    handleChange(getInitialData(), []);
    // Only re-run this when the field itself changes identity (e.g. a
    // different json-form field is swapped in), not on every re-render where
    // `field.data` may be a referentially-new but equivalent object - doing
    // so would clobber in-progress edits with the original server data.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [field.name]);

  return (
    <div>
      {field.label && (
        <FieldLabel required={field.required}>{field.label}</FieldLabel>
      )}
      <WeldJsonForms
        schema={field.schema}
        uischema={field.uischema}
        data={formData}
        onChange={handleChange}
      />
    </div>
  );
}
